/**
 * Listing filter bar — detailed-view toggle, sort submit, filter drawer open.
 */
(() => {
  const TAG = 'listing-filter-bar';
  const STORAGE_KEY = 'product-grid-detailed';
  const OPEN_EVENT = 'filter_drawer_open';

  if (customElements.get(TAG)) return;

  const sectionRoot = (element) => element.closest('.shopify-section');

  class ListingFilterBar extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', this.#onClick);
      this.addEventListener('change', this.#onChange);
      window.addEventListener('pageshow', this.#onPageShow);
      this.#scheduleStoredView();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.#onClick);
      this.removeEventListener('change', this.#onChange);
      window.removeEventListener('pageshow', this.#onPageShow);
      document.removeEventListener('DOMContentLoaded', this.#onDocumentReady);
    }

    #grid() {
      return sectionRoot(this)?.querySelector('[data-product-grid]') ?? null;
    }

    #toggle() {
      return this.querySelector('[data-detailed-view-toggle]');
    }

    #onPageShow = () => {
      this.#applyStoredView();
    };

    #onDocumentReady = () => {
      this.#applyStoredView();
    };

    #scheduleStoredView() {
      if (this.#applyStoredView()) return;

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', this.#onDocumentReady, { once: true });
        return;
      }

      requestAnimationFrame(() => this.#applyStoredView());
    }

    #applyStoredView() {
      if (!this.isConnected) return false;

      const stored = sessionStorage.getItem(STORAGE_KEY);
      const detailed = stored === 'true';
      const toggle = this.#toggle();
      const grid = this.#grid();

      if (toggle instanceof HTMLInputElement) {
        toggle.checked = detailed;
      }

      if (!grid) return false;

      this.#setGridDetailed(detailed);
      return toggle instanceof HTMLInputElement;
    }

    #setGridDetailed(detailed, animate = false) {
      const grid = this.#grid();
      if (!grid) return;

      const apply = () => {
        grid.classList.toggle('product-grid--detailed', detailed);
      };

      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const canTransition =
        animate && !reduceMotion && typeof document.startViewTransition === 'function';

      if (!canTransition) {
        apply();
        return;
      }

      document.startViewTransition(apply);
    }

    #onClick = (event) => {
      const opener = event.target.closest('[data-filter-drawer-open]');
      if (!opener || !this.contains(opener)) return;

      event.preventDefault();
      document.dispatchEvent(
        new CustomEvent(OPEN_EVENT, {
          bubbles: true,
          detail: { opener },
        })
      );
    };

    #onChange = (event) => {
      const toggle = event.target;
      if (toggle instanceof HTMLInputElement && toggle.hasAttribute('data-detailed-view-toggle')) {
        const detailed = toggle.checked;
        sessionStorage.setItem(STORAGE_KEY, String(detailed));
        this.#setGridDetailed(detailed, true);
        return;
      }

      const select = event.target;
      if (!(select instanceof HTMLSelectElement) || !select.hasAttribute('data-sort-select')) return;

      const url = new URL(window.location.href);

      url.searchParams.set('sort_by', select.value);
      url.searchParams.delete('page');
      url.searchParams.delete('sections');
      url.searchParams.delete('section_id');

      window.location.assign(`${url.pathname}${url.search}`);
    };
  }

  customElements.define(TAG, ListingFilterBar);
})();
